"use client";

import { KeyboardArrowRight } from "@ellaandil/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { type ElementType, Fragment } from "react";
import { twMerge } from "tailwind-merge";
import { useTranslations } from "@/i18n/client";
import { Skeleton } from "../Skeleton";
import { BreadcrumbsSchema } from "./BreadcrumbsSchema";

const breadcrumbsVariants = cva("flex max-w-full items-center overflow-hidden", {
	variants: {
		size: {
			small: "",
			large: "",
		},
	},
	defaultVariants: { size: "small" },
});

const listVariants = cva("flex items-center [direction:ltr] shrink-0 min-w-full", {
	variants: {
		size: {
			small: "",
			large: "gap-1",
		},
	},
	defaultVariants: { size: "small" },
});

const itemVariants = cva(
	[
		"inline-flex items-center justify-center",
		"px-grid-component-spacing-4xs py-grid-component-spacing-5xs",
		"rounded-component-specific-button",
		"font-body font-regular",
		"leading-[1.5]",
		"whitespace-nowrap",
	],
	{
		variants: {
			size: {
				small: "text-xs",
				large: "text-sm",
			},
			current: {
				true: "text-text-dark-text-1",
				false: "text-text-dark-text-2 hover:text-text-dark-text-1",
			},
		},
		defaultVariants: { size: "small", current: false },
	},
);

const separatorVariants = cva(
	"inline-flex items-center justify-center shrink-0 p-grid-component-spacing-5xs rounded-component-specific-button text-text-dark-text-2",
	{
		variants: {
			size: {
				small: "[&>svg]:size-4",
				large: "[&>svg]:size-5",
			},
		},
		defaultVariants: { size: "small" },
	},
);

export type BreadcrumbItem = {
	label: string;
	href?: string;
	onClick?: () => void;
};

type BreadcrumbsProps = VariantProps<typeof breadcrumbsVariants> & {
	items: BreadcrumbItem[];
	linkAs?: ElementType;
	className?: string;
	/**
	 * Skip the JSON-LD BreadcrumbList output. Use for in-UI breadcrumbs that
	 * aren't part of the page's navigational hierarchy (e.g. modal/wizard steps).
	 */
	disableSchema?: boolean;
};

function renderBreadcrumbItems({
	items,
	size,
	LinkComponent,
	truncateIndex,
}: {
	items: BreadcrumbItem[];
	size: BreadcrumbsProps["size"];
	LinkComponent: ElementType;
	truncateIndex?: number;
}) {
	return items.map((item, index) => {
		const isCurrent = index === items.length - 1;
		const isTruncatingItem = index === truncateIndex;
		const itemClassName = isTruncatingItem ? "min-w-0 flex-1 overflow-hidden" : "shrink-0";

		return (
			<Fragment key={`${item.label}-${index}`}>
				{index > 0 && (
					<li aria-hidden="true" className={separatorVariants({ size })}>
						<KeyboardArrowRight />
					</li>
				)}
				<li className={itemClassName}>
					{isCurrent ? (
						<span
							className={twMerge(
								itemVariants({ size, current: true }),
								isTruncatingItem && "flex max-w-full min-w-0 overflow-hidden",
							)}
							aria-current="page"
						>
							<span className={twMerge(isTruncatingItem && "block min-w-0 flex-1 truncate")}>
								{item.label}
							</span>
						</span>
					) : item.href ? (
						<LinkComponent
							href={item.href}
							className={twMerge(
								itemVariants({ size, current: false }),
								isTruncatingItem && "flex max-w-full min-w-0 overflow-hidden",
							)}
						>
							<span className={twMerge(isTruncatingItem && "block min-w-0 flex-1 truncate")}>
								{item.label}
							</span>
						</LinkComponent>
					) : item.onClick ? (
						<button
							type="button"
							onClick={item.onClick}
							className={twMerge(
								itemVariants({ size, current: false }),
								isTruncatingItem && "flex max-w-full min-w-0 overflow-hidden",
							)}
						>
							<span className={twMerge(isTruncatingItem && "block min-w-0 flex-1 truncate")}>
								{item.label}
							</span>
						</button>
					) : (
						<span
							className={twMerge(
								itemVariants({ size, current: false }),
								isTruncatingItem && "flex max-w-full min-w-0 overflow-hidden",
							)}
						>
							<span className={twMerge(isTruncatingItem && "block min-w-0 flex-1 truncate")}>
								{item.label}
							</span>
						</span>
					)}
				</li>
			</Fragment>
		);
	});
}

export function Breadcrumbs({
	items,
	size,
	linkAs: LinkComponent = "a",
	className,
	disableSchema = false,
}: BreadcrumbsProps) {
	const t = useTranslations();
	const mobileItems = items.length > 2 ? items.slice(-2) : items;
	const showMobileEllipsis = items.length > 2;
	const mobileTruncateIndex = mobileItems.length > 1 ? 0 : undefined;

	return (
		<nav aria-label={t("Breadcrumb")} className={twMerge(breadcrumbsVariants({ size }), className)}>
			{!disableSchema && <BreadcrumbsSchema items={items} />}
			<ol className={twMerge(listVariants({ size }), "shrink min-w-0 sm:hidden")}>
				{showMobileEllipsis && (
					<>
						<li>
							<span
								className={twMerge(itemVariants({ size, current: false }), "shrink-0")}
								aria-hidden="true"
							>
								&hellip;
							</span>
						</li>
						<li aria-hidden="true" className={separatorVariants({ size })}>
							<KeyboardArrowRight />
						</li>
					</>
				)}
				{renderBreadcrumbItems({
					items: mobileItems,
					size,
					LinkComponent,
					truncateIndex: mobileTruncateIndex,
				})}
			</ol>
			<ol className={twMerge(listVariants({ size }), "hidden sm:flex")}>
				{renderBreadcrumbItems({ items, size, LinkComponent })}
			</ol>
		</nav>
	);
}

export const BreadcrumbsSkeleton = ({
	length = 3,
	size,
	linkAs: LinkComponent = "a",
	className,
}: Omit<BreadcrumbsProps, "items"> & { length?: number }) => {
	const skeletonItems = Array.from({ length }, () => ({
		label: "Loading",
		href: undefined,
	}));

	return (
		<Skeleton fitContent>
			<Breadcrumbs items={skeletonItems} size={size} linkAs={LinkComponent} className={className} />
		</Skeleton>
	);
};

Breadcrumbs.displayName = "Breadcrumbs";
